import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Container, Row, Button, Col, Modal } from 'react-bootstrap'
import { useNavigate } from 'react-router';
import { Applications } from '../../api/Applications';
import BdaApplicationItem from '../../components/list-items/BdaApplicationItem';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { Select } from 'antd';
import { accessLevelOptions } from '../../utils';
import { cloneDeep } from 'lodash';
import { EventEmitter } from '../../utils/EventEmitter';
import { useCallback } from 'react';
import { BdaApplication } from '../../models/BdaApplication';
import { PagedResults } from '../../models/generics';
import AppBrowsingToolbar, { Filters } from '../../components/common/AppBrowsingToolbar';
import { DefaultOptionType } from 'antd/es/select';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';
import AppLoader from '../../components/common/AppLoader';
import ScriptEditor from '../../components/common/ScriptEditor';

const statusOptions = [
  { label: "Ready", value: "READY" },
  { label: "Starting", value: "STARTING" },
  { label: "Running", value: "RUNNING" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Stopped", value: "STOPPED" },
  { label: "Error", value: "ERROR" },
];

const DEFAULT_FILTERS: Filters = {
  'status': {
    placeholder: 'Select status',
    select: true,
    query: 'status',
    type: 'string',
    options: statusOptions,
    customOptions: false,
    selectProps: {
      mode: 'multiple',
    }
  },
  'accessLevel': {
    placeholder: 'Select privacy level',
    select: true,
    query: 'accessLevel',
    type: 'string',
    options: accessLevelOptions
      .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true),
    customOptions: true,
    renderOption: (option: DefaultOptionType, index: number) => (
      <Select.Option key={index} value={option.value} label={option.label}>
        <div>
          <FontAwesomeIcon icon={option.icon} fixedWidth />
          <span className="ms-2">{option.label}</span>
        </div>
      </Select.Option>
    )
  },
  'tags': {
    placeholder: 'Select  tags...',
    select: true,
    query: 'tags',
    type: 'string',
    options: [],
    selectProps: {
      mode: "tags",
    }
  },
  'defaultOnly': { label: 'Default Only', checkbox: true, type: 'boolean', query: 'defaultVersion', value: true },
  'templatesOnly': { label: 'Templates Only', checkbox: true, type: 'boolean', query: 'templatesOnly' },
  'owned': { checkbox: true, type: 'boolean', query: 'createdBy' },
}

export default function BdaApplicationsList() {
  const [applications, setApplications] = useState<BdaApplication[]>();
  const [error, setError] = useState(false);
  const [latestParams, setLatestParams] = useState<URLSearchParams>();

  const [totalApplications, setTotalApplications] = useState<number>();

  const [applicationDefinition, setApplicationDefinition] = useState<string>();
  const [applicationToDelete, setApplicationToDelete] = useState<BdaApplication>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const handleDeleteModalClose = () => { setShowDeleteModal(false); setApplicationToDelete(undefined) };
  const handleImportModalClose = () => setShowImportModal(false);
  const openImportModal = () => setShowImportModal(true);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const navigate = useNavigate();

  const assetLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));
  const applicationsFilters: Filters = useMemo(() => ({
    ...DEFAULT_FILTERS,
    'search': {
      placeholder: `Search ${assetLabels.singular}...`,
      query: 'freeText',
    },
    'owned': {
      ...DEFAULT_FILTERS.owned!,
      label: `My ${assetLabels.plural}`,
    },
  }), [assetLabels]);

  const handleStatusMessageEvent = useCallback((evt: any) => {
    const statusMessage = evt.details;
    if (statusMessage.bda && !statusMessage.serviceId) {
      const index = applications!.findIndex(app => app.id === statusMessage.bda.id);
      if (index > -1) {
        const newApplications = cloneDeep(applications!);
        newApplications[index].status = statusMessage.bda.status;
        setApplications(newApplications);
      }
    }
  }, [applications]);

  useEffect(() => {
    if (applications) EventEmitter.subscribe('BDA_APP_STATUS_CHANGE', handleStatusMessageEvent)

    return () => EventEmitter.unsubscribe('BDA_APP_STATUS_CHANGE');
  }, [handleStatusMessageEvent, applications]);

  const getApplications = useCallback((queryParams?: Record<string, any>) => {
    const params = new URLSearchParams(queryParams || latestParams);
    if (queryParams) setLatestParams(params);

    Applications.getApplications(params)
      .then(
        ({ data: appPages }) => {
          setApplications((appPages as PagedResults<BdaApplication>).content);
          setTotalApplications((appPages as PagedResults<BdaApplication>).pageMetadata.totalElements);
        },
        (err) => {
          showToast('apps-error', `Could not get ${assetLabels.plural}`, `${assetLabels.plural} Error`, 'danger');
          setError(true);
        }
      );
  }, [showToast, latestParams, assetLabels]);

  const reset = useCallback((params?: Record<string, any>) => {
    setError(false);
    setApplications(undefined);
    getApplications(params);
  }, [getApplications]);

  useEffect(() => {
    EventEmitter.subscribe('browsing:paramsChanged', reset);
    return () => EventEmitter.unsubscribe('browsing:paramsChanged');
  }, [reset]);

  const navigateToCreation = () => navigate('/bda-applications/create');
  const navigateToDetail = useCallback((app: BdaApplication) => () => navigate(`/bda-applications/${app.id}`), [navigate]);

  const navigateToEdit = useCallback((app: BdaApplication, fromTemplate?: boolean) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    navigate(`/bda-applications/${app.id}/edit${fromTemplate ? '?fromTemplate=true' : ''}`);
  }, [navigate]);

  const createTemplateFromApplication = useCallback((app: BdaApplication) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    showLoader();

    Applications.createTemplate(app.id!)
      .then(
        ({ data }) => {
          hideLoader();
          showToast('create-template-success', 'Template created successfully.', 'Template Success', 'success');
          navigate(`/bda-applications/${data.id}`);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('create-template-error', message, `Template Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [hideLoader, showLoader, showToast, navigate]);

  const importApplication = () => {
    localStorage.setItem('applicationToImport', applicationDefinition!);
    setShowImportModal(false);
    navigate('/bda-applications/create?importMode=true');
  }

  const openDeleteModal = (app: BdaApplication) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setApplicationToDelete(app);
  }

  const deleteApplication = () => {
    showLoader();

    Applications.deleteApplication(applicationToDelete!.id!)
      .then(
        () => {
          reset();
          hideLoader();
          handleDeleteModalClose();
          showToast('bda-delete-success', `${applicationToDelete!.template ? 'Template' : assetLabels.singular} deleted successfully.`, 'Success', 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('bda-delete-error', message, `${applicationToDelete!.template ? 'Template' : assetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }

  const runApplication = useCallback(async (app: BdaApplication) => {
    showLoader();

    // TODO avoid GET detail only to retrieve workflowId 🤮
    try {
      const { data: application } = await Applications.getApplication(app.id!);
      const workflowId = application.workflows[0].id!;

      Applications.runApplication(app.id!, workflowId)
        .then(
          (_) => {
            showToast('bda-run-success', `${assetLabels.singular} started successfully.`, 'Success', 'success');
            hideLoader();
          },
          (err) => {
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            showToast('bda-run-error', message, `${assetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
            hideLoader();
          }
        );
    } catch (e: any) {
      const message = e.response?.data?.message || e.message;
      const status = e.response?.status;
      showToast('bda-run-error', message, `${assetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
      hideLoader();
    }
  }, [showToast, showLoader, hideLoader, assetLabels]);

  const stopApplication = useCallback(async (app: BdaApplication) => {
    showLoader();

    try {
      const { data: application } = await Applications.getApplication(app.id!);
      const workflowId = application.workflows[0].id!;

      Applications.stopApplication(app.id!, workflowId)
        .then(
          (_) => {
            showToast('bda-stop-success', `${assetLabels.singular} stopped successfully.`, 'Success', 'success');
            hideLoader();
          },
          (err) => {
            showToast('bda-stop-error', `Could not stop ${assetLabels.singular}.`, `${assetLabels.singular} Error`, 'danger');
            hideLoader();
          }
        );
    } catch (e) {
      console.log(e);
      showToast('bda-stop-error', `Could not stop ${assetLabels.singular}.`, `${assetLabels.singular} Error`, 'danger');
      hideLoader();
    }
  }, [showToast, showLoader, hideLoader, assetLabels]);

  const toggleApplication = useCallback((app: BdaApplication) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    ["RUNNING", "PARTIAL", "STARTING"].includes(app.status as string) ? stopApplication(app) : runApplication(app);
  }, [runApplication, stopApplication]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    );

    if (!applications) return <AppLoader className="py-5" justify="center" size={100} />;

    return applications.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No {assetLabels.plural} found.</span>
        </Alert>
      )
      : (
        <Row>
          {
            applications.map(app => (
              <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={app.id}>
                <BdaApplicationItem
                  application={app}
                  onClick={navigateToDetail(app)}
                  onApplicationToggle={toggleApplication(app)}
                  onTemplateAction={app.template ? navigateToEdit(app, true) : createTemplateFromApplication(app)}
                  onApplicationEdit={navigateToEdit(app)}
                  onApplicationDelete={openDeleteModal(app)}
                />
              </Col>
            ))
          }
        </Row>
      )
  }, [applications, error, assetLabels, createTemplateFromApplication, navigateToDetail, navigateToEdit, toggleApplication]);

  return (
    <Container className="mt-3">
      <AppBrowsingToolbar
        title={`${assetLabels.plural} List`}
        actions={
          <Col xs={6} md="auto" className="text-end">
            <Button size="sm" variant="light" className="border me-2" onClick={openImportModal}>
              <FontAwesomeIcon icon={solid('upload')} />
              <span className="ms-2">Upload {assetLabels.singular}</span>
            </Button>
            <Button size="sm" variant="success" onClick={navigateToCreation}>
              <FontAwesomeIcon icon={solid('plus')} />
              <span className="ms-2">Create {assetLabels.singular}</span>
            </Button>
          </Col>
        }
        filters={applicationsFilters}
        total={totalApplications}
      />

      <hr />

      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {applicationToDelete?.template ? 'Template' : assetLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {applicationToDelete?.template ? 'Template' : assetLabels.singular} <b>{applicationToDelete?.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteModalClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteApplication}>Delete</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showImportModal} onHide={handleImportModalClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Import {assetLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ScriptEditor value={applicationDefinition} onChange={setApplicationDefinition} />
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="light" className="border" onClick={handleImportModalClose}>Close</Button>
          <Button size="sm" variant="success" onClick={importApplication}>Import</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
